import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { pocApi, percent, MISS_REASON_LABELS, OCCLUSION_LABELS } from '../utils/pocApi';

// PH1.5: the store, not the frame.
//
// PH1 can look healthy while the pipeline is unusable — a detector that finds
// every panel it is shown still misses a machine whose panel was behind a
// person for the whole walk. So this screen is a person walking the video and
// answering, once per physical machine, "was this ever found, and if not why".
//
// The answers are typed rather than free text because the output of PH1.5 is a
// tally: which cause costs the most machines is what decides what to fix.

export default function PocCoverage() {
  useAuthGuard();
  const { datasetId } = useParams();

  const [dataset, setDataset] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [bulkCount, setBulkCount] = useState(20);

  const load = useCallback(async () => {
    try {
      const [detail, positions] = await Promise.all([
        pocApi.getDataset(datasetId),
        pocApi.playPositions(datasetId),
      ]);
      setDataset(detail.dataset);
      setRows(positions.playPositions || []);
      setDirty(false);
    } catch (e) {
      setError(e.message);
    }
  }, [datasetId]);

  useEffect(() => { load(); }, [load]);

  const patch = (code, changes) => {
    setRows((prev) => prev.map((r) => (r.code === code ? { ...r, ...changes } : r)));
    setDirty(true);
  };

  // Adding rows is a range, not one at a time: an operator counting machines in
  // a video knows roughly how many there are before they start answering.
  const addRows = () => {
    const existing = new Set(rows.map((r) => r.code));
    let highest = 0;
    rows.forEach((r) => {
      const n = Number(String(r.code).replace(/\D/g, ''));
      if (!Number.isNaN(n)) highest = Math.max(highest, n);
    });

    const added = [];
    for (let i = 1; added.length < bulkCount; i++) {
      const code = `P${String(highest + i).padStart(3, '0')}`;
      if (existing.has(code)) continue;
      added.push({
        code,
        cabinetGroup: '',
        detected: false,
        occlusion: 'none',
        missReason: '',
        panelEverVisible: true,
        note: '',
      });
    }
    setRows((prev) => [...prev, ...added]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await pocApi.savePlayPositions(datasetId, rows.map((r) => ({
        code: r.code,
        cabinetGroup: r.cabinetGroup || '',
        detected: !!r.detected,
        occlusion: r.occlusion || 'none',
        missReason: r.detected ? '' : (r.missReason || ''),
        panelEverVisible: r.panelEverVisible !== false,
        note: r.note || '',
      })));
      setRows(result.playPositions || []);
      setDirty(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (row.id) {
      try {
        await pocApi.deletePlayPosition(row.id);
      } catch (e) {
        setError(e.message);
        return;
      }
    }
    setRows((prev) => prev.filter((r) => r.code !== row.code));
  };

  // Live, so the operator sees the tally move as they answer rather than only
  // after a save.
  const live = useMemo(() => {
    const total = rows.length;
    const detected = rows.filter((r) => r.detected).length;
    const panelBlind = rows.filter((r) => r.panelEverVisible === false).length;
    const unanswered = rows.filter((r) => !r.detected && !r.missReason).length;
    const reasons = {};
    rows.filter((r) => !r.detected && r.missReason).forEach((r) => {
      reasons[r.missReason] = (reasons[r.missReason] || 0) + 1;
    });
    return { total, detected, panelBlind, unanswered, reasons };
  }, [rows]);

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">{dataset?.name} <span className="text-muted">— PH1.5 物理台カバレッジ</span></h5>
          <small className="text-muted">動画全体で、各 PlayPosition が一度でも検出できたか</small>
        </div>
        <div className="d-flex gap-2">
          <Link className="btn btn-sm btn-outline-secondary" to={`/poc/${datasetId}/report`}>PH0/PH1</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/poc">一覧</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="row g-3 mb-4">
        <Stat
          label="検出できた物理台"
          value={`${live.detected} / ${live.total}`}
          hint={live.total ? percent(live.detected / live.total) : '—'}
        />
        <Stat
          label="Panel方式の上限"
          value={live.total ? percent((live.total - live.panelBlind) / live.total) : '—'}
          hint={`全出現フレームでPanelが隠れていた台 ${live.panelBlind} 件`}
        />
        <Stat
          label="未検出"
          value={`${live.total - live.detected} 件`}
          hint={live.unanswered ? `理由未記入 ${live.unanswered} 件` : '理由はすべて記入済み'}
        />
        <div className="col-6 col-md-3">
          <div className="border rounded p-3 h-100">
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>未検出の理由</div>
            {Object.keys(live.reasons).length === 0 ? (
              <div className="text-muted small mt-1">—</div>
            ) : (
              <div className="mt-1">
                {Object.entries(live.reasons)
                  .sort((a, b) => b[1] - a[1])
                  .map(([key, n]) => (
                    <div key={key} className="d-flex justify-content-between" style={{ fontSize: '0.8rem' }}>
                      <span>{MISS_REASON_LABELS[key] || key}</span>
                      <span className="fw-semibold">{n}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <input
            type="number"
            min="1"
            max="200"
            className="form-control form-control-sm"
            style={{ width: 80 }}
            value={bulkCount}
            onChange={(e) => setBulkCount(Math.max(1, Number(e.target.value) || 1))}
          />
          <button className="btn btn-sm btn-outline-secondary" onClick={addRows}>台を追加</button>
        </div>
        <div className="d-flex align-items-center gap-2">
          {dirty && <span className="text-warning-emphasis small">未保存の変更があります</span>}
          <button className="btn btn-sm btn-primary" onClick={save} disabled={saving || !dirty}>
            {saving ? '保存中…' : '保存'}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="alert alert-light border">
          まだ台が登録されていません。動画を見ながら物理台の数だけ行を追加してください。
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 80 }}>Code</th>
                <th style={{ width: 130 }}>筐体グループ</th>
                <th style={{ width: 150 }}>検出</th>
                <th style={{ width: 140 }}>遮蔽</th>
                <th style={{ width: 160 }}>未検出理由</th>
                <th style={{ width: 150 }}>Panel可視</th>
                <th>メモ</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className={r.detected ? '' : 'table-warning'}>
                  <td className="fw-semibold">{r.code}</td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      placeholder="TRIPLE 等"
                      value={r.cabinetGroup || ''}
                      onChange={(e) => patch(r.code, { cabinetGroup: e.target.value })}
                    />
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm">
                      <button
                        className={`btn ${r.detected ? 'btn-success' : 'btn-outline-success'}`}
                        onClick={() => patch(r.code, { detected: true, missReason: '' })}
                      >
                        ○ 検出
                      </button>
                      <button
                        className={`btn ${!r.detected ? 'btn-danger' : 'btn-outline-danger'}`}
                        onClick={() => patch(r.code, { detected: false })}
                      >
                        × 未検出
                      </button>
                    </div>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={r.occlusion || 'none'}
                      onChange={(e) => patch(r.code, { occlusion: e.target.value })}
                    >
                      {Object.entries(OCCLUSION_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={r.missReason || ''}
                      disabled={r.detected}
                      onChange={(e) => patch(r.code, { missReason: e.target.value })}
                    >
                      <option value="">—</option>
                      {Object.entries(MISS_REASON_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={r.panelEverVisible !== false}
                        onChange={(e) => patch(r.code, { panelEverVisible: e.target.checked })}
                      />
                      <label className="form-check-label small">一度は見えた</label>
                    </div>
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.note || ''}
                      onChange={(e) => patch(r.code, { note: e.target.value })}
                    />
                  </td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-link text-danger p-0" onClick={() => remove(r)}>削除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-muted mt-3" style={{ fontSize: '0.75rem' }}>
        「Panel可視」を外すと、その台は全出現フレームで操作部が隠れていたことになります。Panel方式ではどれだけ検出器を良くしても届かないため、
        Panel と Cabinet のどちらを検出対象にするかは、この件数で決まります。
      </p>
    </Layout>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="col-6 col-md-3">
      <div className="border rounded p-3 h-100">
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{label}</div>
        <div className="fs-4 fw-semibold">{value}</div>
        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{hint}</div>
      </div>
    </div>
  );
}
